import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Columns3, FilterX, MoreVertical, Plus, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import PageHeader from "@/components/common/PageHeader";
import Paginator from "@/components/common/Paginator";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsDialog from "@/components/table/ColumnSettingsDialog";
import DataTable from "@/components/table/DataTable";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import type { UserRole } from "@/lib/auth";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showError, showSuccess } from "@/lib/toast";
import {
  deleteUser,
  issueTemporaryPassword,
  listUsers,
  updateUser,
  type AppUser,
  type UserInput,
} from "./api";
import { buildUserColumns, userColumnIds, userPinnedColumnIds } from "./columns";
import { ROLE_OPTIONS } from "./roles";
import TemporaryPasswordDialog from "./TemporaryPasswordDialog";
import UserCreateDialog from "./UserCreateDialog";
import { USERS_PAGE_SIZE, useUserListParams } from "./useUserListParams";

/** 発行した仮パスワードを一度だけ見せるための控え。 */
interface IssuedPassword {
  email: string;
  temporaryPassword: string;
  expiresAt: string | null;
}

/**
 * ユーザー管理。
 *
 * パスワードは一覧からは触れない。管理者は仮パスワードの発行・再発行、本人は
 * パスワード変更画面、と経路を分けている（管理者が他人のパスワードを直接
 * 決められると、その値を知った状態になるため）。
 */
export default function UserList() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const editable = canWrite(currentUser, "userAdmin");
  const { params, update, reset: resetParams } = useUserListParams();
  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings("users", userColumnIds, userPinnedColumnIds);
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();

  const [creating, setCreating] = useState(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedPassword | null>(null);
  const [editingRole, setEditingRole] = useState<AppUser | null>(null);
  const [toggling, setToggling] = useState<AppUser | null>(null);
  const [reissuing, setReissuing] = useState<AppUser | null>(null);
  const [deleting, setDeleting] = useState<AppUser | null>(null);

  const query = useQuery({
    queryKey: ["users", params],
    queryFn: () =>
      listUsers({
        keyword: params.keyword || undefined,
        sortKey: params.sortKey,
        ascending: params.ascending,
        limit: USERS_PAGE_SIZE,
        offset: (params.page - 1) * USERS_PAGE_SIZE,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const saveField = async (
    target: AppUser,
    input: UserInput,
  ): Promise<string | null> => {
    try {
      await updateUser(target.user_id, input);
      await invalidate();
      return null;
    } catch (e) {
      // 入力し直せば解決する失敗（重複メールなど）は呼び出し元に返す。
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const toggle = useMutation({
    mutationFn: (target: AppUser) =>
      updateUser(target.user_id, { disabled: !target.disabled }),
    onSuccess: (updated) => {
      showSuccess(
        `${updated.email} を${updated.disabled ? "無効" : "有効"}にしました。`,
      );
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setToggling(null),
  });

  const reissue = useMutation({
    mutationFn: (target: AppUser) => issueTemporaryPassword(target.user_id),
    onSuccess: (res, target) => {
      setIssued({
        email: target.email,
        temporaryPassword: res.temporary_password,
        expiresAt: res.expires_at,
      });
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setReissuing(null),
  });

  const remove = useMutation({
    mutationFn: (target: AppUser) => deleteUser(target.user_id),
    onSuccess: (_, target) => {
      showSuccess(`${target.email} を削除しました。`);
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeleting(null),
  });

  const columns = useMemo(
    () =>
      buildUserColumns({
        canWrite: editable,
        currentUserId: currentUser?.user_id,
        onEditField: saveField,
        onEditRole: setEditingRole,
        onToggleDisabled: setToggling,
        onIssueTemporaryPassword: setReissuing,
        onDelete: setDeleting,
      }),
    // saveField は毎回作り直されるが、中身は参照している state に依存しない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable, currentUser?.user_id],
  );

  const sorting: SortingState = [{ id: params.sortKey, desc: !params.ascending }];

  const table = useReactTable<AppUser>({
    data: query.data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.user_id,
    columnResizeMode: "onChange",
    manualSorting: true,
    manualPagination: true,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) return;
      const sortKey = table.getColumn(first.id)?.columnDef.meta?.sortKey;
      if (!sortKey) return;
      update({ sortKey, ascending: !first.desc });
    },
    enableSorting: true,
    defaultColumn: { enableSorting: false, minSize: 60, maxSize: 600 },
  });

  for (const column of table.getAllLeafColumns()) {
    column.columnDef.enableSorting = Boolean(column.columnDef.meta?.sortKey);
  }

  const roleTarget: SingleFieldTarget | null = editingRole
    ? {
        label: "権限",
        value: editingRole.role,
        kind: "select",
        required: true,
        options: ROLE_OPTIONS.map((o) => ({ key: o.key, label: o.label })),
        description:
          editingRole.user_id === currentUser?.user_id
            ? "自分自身の管理者権限は解除できません。"
            : "管理者はユーザー管理とマスタ編集ができます。",
      }
    : null;

  const total = query.data?.pagination.total ?? 0;

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title="ユーザー管理"
            actions={
              <>
                <Input
                  aria-label="メールアドレス・表示名で検索"
                  placeholder="メールアドレス・表示名"
                  variant="bordered"
                  radius="sm"
                  className="w-64"
                  startContent={<Search size={16} className="text-default-400" />}
                  defaultValue={params.keyword}
                  onBlur={(e) =>
                    update({ keyword: (e.target as HTMLInputElement).value.trim() })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      update({ keyword: (e.target as HTMLInputElement).value.trim() });
                    }
                  }}
                  isClearable
                  onClear={() => update({ keyword: "" })}
                />
                <AuthorizedButton
                  scope="userAdmin"
                  radius="full"
                  color="primary"
                  startContent={<Plus size={18} />}
                  onPress={() => setCreating(true)}
                >
                  新規登録
                </AuthorizedButton>

                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      radius="full"
                      aria-label="表示設定"
                    >
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="表示設定"
                    onAction={(key) => {
                      if (key === "columns") setColumnSettingsOpen(true);
                      if (key === "reload") void query.refetch();
                      if (key === "reset_filters") resetParams();
                      if (key === "reset_columns") resetColumns();
                    }}
                  >
                    <DropdownItem key="columns" startContent={<Columns3 size={18} />}>
                      表示列の設定
                    </DropdownItem>
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    <DropdownItem
                      key="reset_filters"
                      startContent={<FilterX size={18} />}
                    >
                      検索条件リセット
                    </DropdownItem>
                    <DropdownItem
                      key="reset_columns"
                      startContent={<Columns3 size={18} />}
                    >
                      表示列リセット
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />

          <div className="min-h-0 flex-1">
            {query.isError ? (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <p className="text-default-600">
                  データの取得に失敗しました。
                  <br />
                  {(query.error as Error).message}
                </p>
                <Button color="primary" onPress={() => void query.refetch()}>
                  再読み込み
                </Button>
              </div>
            ) : query.isPending ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label="読み込み中..." />
              </div>
            ) : (
              <DataTable
                table={table}
                isLoading={query.isFetching}
                emptyMessage="ユーザーがいません"
                scrollRef={scrollRef}
              />
            )}
          </div>
        </section>

        <Paginator
          total={total}
          pageSize={USERS_PAGE_SIZE}
          page={params.page}
          onChange={(page) => update({ page })}
        />
      </div>

      <EdgeScrollButton
        side="left"
        isDisabled={!canScrollLeft}
        onPress={() => scrollBy(-1)}
      />
      <EdgeScrollButton
        side="right"
        isDisabled={!canScrollRight}
        onPress={() => scrollBy(1)}
      />

      <ColumnSettingsDialog
        isOpen={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        table={table}
        onReset={resetColumns}
      />

      <UserCreateDialog
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setIssued({
            email: created.user.email,
            temporaryPassword: created.temporary_password,
            expiresAt: created.user.temp_password_expires_at,
          });
          void invalidate();
        }}
      />

      <TemporaryPasswordDialog
        isOpen={issued != null}
        email={issued?.email ?? ""}
        temporaryPassword={issued?.temporaryPassword ?? null}
        expiresAt={issued?.expiresAt ?? null}
        onClose={() => setIssued(null)}
      />

      <SingleFieldEditDialog
        target={roleTarget}
        onClose={() => setEditingRole(null)}
        onSave={(value) =>
          editingRole
            ? saveField(editingRole, { role: (value ?? "view") as UserRole })
            : Promise.resolve(null)
        }
      />

      <ConfirmDialog
        isOpen={toggling != null}
        title={toggling?.disabled ? "ユーザーを有効にする" : "ユーザーを無効にする"}
        message={
          toggling?.disabled
            ? `${toggling.email} を有効にします。再びログインできるようになります。`
            : `${toggling?.email} を無効にします。\nログインできなくなりますが、過去の見積・案件の担当者としては残ります。`
        }
        confirmLabel={toggling?.disabled ? "有効にする" : "無効にする"}
        isBusy={toggle.isPending}
        onClose={() => setToggling(null)}
        onConfirm={() => {
          if (toggling) toggle.mutate(toggling);
        }}
      />

      <ConfirmDialog
        isOpen={reissuing != null}
        title="仮パスワードの再発行"
        message={`${reissuing?.email} の仮パスワードを再発行します。\n\n・いまのパスワードは使えなくなります\n・このユーザーのログインはすべて解除されます\n・新しい仮パスワードはこの直後に一度だけ表示されます`}
        confirmLabel="再発行"
        isBusy={reissue.isPending}
        onClose={() => setReissuing(null)}
        onConfirm={() => {
          if (reissuing) reissue.mutate(reissuing);
        }}
      />

      <ConfirmDialog
        isOpen={deleting != null}
        title="ユーザーの削除"
        message={`${deleting?.email} を削除します。\nこの操作は取り消せません。よろしいですか？\n\n退職などで使わなくなっただけなら、削除ではなく「無効」にしてください。`}
        confirmLabel="削除"
        isDanger
        isBusy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
      />
    </div>
  );
}
